import * as React from "react"
import { createPortal } from "react-dom"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/30 backdrop-blur-[2px]  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const SAVE_BTN = '[data-enter-submit], button[type="submit"], [data-testid$="-save"], [data-testid$="-confirm"], [data-testid$="-create-btn"], [data-testid$="-save-btn"], [data-testid^="save-"], [data-testid^="confirm-"], [data-testid="wizard-finish-btn"], [data-testid="wizard-next-btn"]';
const SKIP_TYPES = ["checkbox", "radio", "file", "button", "submit", "range", "color"];
const shown = (b) => b && !b.disabled && b.offsetParent !== null;
export const findSave = (scope) => [...scope.querySelectorAll(SAVE_BTN)].find(shown);

// Enter saves one-line fields; Cmd/Ctrl+Enter saves multi-line notes.
export const enterToSubmit = (e) => {
  if (e.key !== "Enter" || e.isComposing || e.defaultPrevented) return;
  const t = e.target;
  const area = t instanceof HTMLTextAreaElement;
  if (area ? !(e.metaKey || e.ctrlKey) : e.shiftKey || !(t instanceof HTMLInputElement) || SKIP_TYPES.includes(t.type)) return;
  const form = t.closest("form");
  if (form) { if (area) { e.preventDefault(); form.requestSubmit(); } return; }
  const btn = findSave(e.currentTarget);
  if (btn) { e.preventDefault(); btn.click(); }
};

const fine = () => window.matchMedia?.("(pointer: fine)").matches;
const FIELD = 'input:not([type=hidden]):not([type=checkbox]):not([type=radio]):not([type=file]):not([type=range]):not([type=color]):not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly])';
export const focusFirstIn = (root, keepIfFocused = false) => {
  if (!fine() || !root) return false;
  if (keepIfFocused && root.contains(document.activeElement) && document.activeElement.matches?.(FIELD)) return true;
  const el = [...root.querySelectorAll(FIELD)].find((x) => x.offsetParent !== null && x.type !== "search" && x.type !== "date" && x.type !== "datetime-local");
  if (el) el.focus({ preventScroll: true });
  return !!el;
};
export const focusFirst = (e) => {
  const root = e.currentTarget || e.target;
  if (!fine()) { e.preventDefault(); return; }
  if (root?.dataset?.noAutofocus !== undefined) return;
  if (focusFirstIn(root)) e.preventDefault();
};

const TEXTUAL = (t) => (t instanceof HTMLTextAreaElement || (t instanceof HTMLInputElement && ["text", "email", "number", "url", "tel", ""].includes(t.type))) && !t.closest("[data-no-guard], [data-inline-edit]");

// Tracks typed text and asks before it is thrown away.
export function useDiscardGuard(enabled = true) {
  const [dirty, setDirty] = React.useState(false);
  const [ask, setAsk] = React.useState(false);
  const closeRef = React.useRef(null);
  const intercept = (e) => {
    if (document.activeElement?.closest?.("[data-inline-edit]") && e.type === "keydown") { e.preventDefault(); return; }
    if (enabled && dirty) { e.preventDefault(); setAsk(true); }
  };
  const settle = (root) => setTimeout(() => { if (root.isConnected && ![...root.querySelectorAll("input, textarea")].some((x) => TEXTUAL(x) && x.value.trim())) setDirty(false); }, 800);
  const props = {
    onInput: (e) => { if (enabled && TEXTUAL(e.target)) setDirty(true); },
    onClickCapture: (e) => { if (e.target.closest?.(SAVE_BTN)) settle(e.currentTarget); },
    onSubmitCapture: (e) => settle(e.currentTarget),
    onEscapeKeyDown: intercept,
    onPointerDownOutside: intercept,
    onInteractOutside: (e) => { if (enabled && dirty) e.preventDefault(); },
  };
  const close = () => (enabled && dirty ? setAsk(true) : closeRef.current?.click());
  const prompt = (
    <>
      <DialogPrimitive.Close ref={closeRef} className="hidden" aria-hidden tabIndex={-1} />
      {ask && createPortal(
        <div className="fixed inset-x-0 bottom-0 z-[100] flex justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))] pointer-events-none" data-testid="discard-prompt">
          <div className="pointer-events-auto w-full max-w-sm rounded-[22px] bg-foreground text-background p-2 pl-5 flex items-center gap-2 shadow-[0_20px_60px_-12px_rgba(0,0,0,0.5)] animate-in fade-in slide-in-from-bottom-4 duration-300" style={{ pointerEvents: "auto" }}>
            <span className="flex-1 text-sm font-medium">Discard what you typed?</span>
            <button type="button" onClick={() => setAsk(false)} data-testid="discard-keep" className="h-9 px-3.5 rounded-full text-sm hover:bg-background/10">Keep editing</button>
            <button type="button" onClick={() => { setAsk(false); setDirty(false); setTimeout(() => closeRef.current?.click(), 0); }} data-testid="discard-confirm" className="h-9 px-3.5 rounded-full text-sm font-semibold bg-[#FF3B30] text-white">Discard</button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
  return { props, close, prompt };
}

const chain = (a, b) => (e) => { a?.(e); if (!e.defaultPrevented) b?.(e); };

const DialogContent = React.forwardRef(({ className, children, hideClose = false, guard = true, onKeyDown, onEscapeKeyDown, onPointerDownOutside, onOpenAutoFocus, ...props }, ref) => {
  const g = useDiscardGuard(guard);
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        {...g.props}
        onKeyDown={(e) => { onKeyDown?.(e); enterToSubmit(e); }}
        onEscapeKeyDown={chain(onEscapeKeyDown, g.props.onEscapeKeyDown)}
        onPointerDownOutside={chain(onPointerDownOutside, g.props.onPointerDownOutside)}
        onOpenAutoFocus={chain(onOpenAutoFocus, focusFirst)}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid grid-cols-[minmax(0,1fr)] w-[calc(100%-2rem)] max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain translate-x-[-50%] translate-y-[-50%] gap-4 bg-background dark:bg-card dark:ring-1 dark:ring-white/10 p-6 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.25)] ring-1 ring-black/[0.04] dark:ring-white/10 duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-3xl",
          className
        )}
        {...props}>
        {children}
        {!hideClose && <button type="button" onClick={g.close}
          data-testid="dialog-close-btn" className="absolute right-4 top-4 h-8 w-8 coarse:h-10 coarse:w-10 rounded-full flex items-center justify-center bg-muted/70 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>}
        {g.prompt}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-tight tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
